import { useRef, useState } from "react";
import {
    Box, Button, Dialog, DialogActions, DialogContent,
    LinearProgress, Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import GetAppIcon from "@mui/icons-material/GetApp";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import { useSnackbar } from "notistack";
import { apiConstants, postGeneric } from "../../common/constants";
import { runBulkInSlices, readFileAsRows, downloadSampleSheetWithDropdowns } from "./bulkRunner";
import { MUTED, gradientBtnSx } from "./SectionCard";
import { DIALOG_PAPER_SX, TicketsDialogTitle, pillBtnSx } from "./TicketsUi";
import { ACCENTS, COLORS } from "./emsTheme";

const SAMPLE_ROWS = [
    { ticket_number: "ESC-100001", comment: "Delivered back to the warehouse", visibility: "EXTERNAL", status: "Resolved", attachment: "https://drive.google.com/file/d/FILE_ID/view" },
    { ticket_number: "ESC-100002", comment: "Escalated to the courier hub", visibility: "INTERNAL", status: "Pending at Ops", attachment: "" },
    { ticket_number: "ESC-100003", comment: "Customer asked for a reattempt", visibility: "EXTERNAL", status: "", attachment: "" },
    { ticket_number: "ESC-100004", comment: "", visibility: "", status: "Closed", attachment: "" },
];

// Rows that download attachments go in smaller slices, so one request never waits on a hundred
// downloads.
const SLICE_SIZE_WITH_FILES = 20;

const linksOf = (value) => String(value || "").split(",").map((s) => s.trim()).filter(Boolean);

/**
 * One upload for comments, attachments and status changes (replaces Bulk Resolve and Bulk
 * Comments). Columns: ticket_number, comment, visibility (INTERNAL | EXTERNAL), status (any status
 * name, blank = unchanged), attachment (Google Drive / Dropbox share links, comma-separated). Each
 * row needs at least one of comment, status or attachment; the server adds the comment and files
 * first, then moves the ticket (/bulk-by-number/update).
 */
const BulkUpdateModal = ({ open, onClose, onDone, statusList = [] }) => {
    const { enqueueSnackbar } = useSnackbar();
    const fileInputRef = useRef(null);
    const cancelRef = useRef(false);
    const [fileName, setFileName] = useState("");
    const [results, setResults] = useState([]);
    const [processing, setProcessing] = useState(false);

    const statusByName = new Map(statusList.map((s) => [String(s.label).toLowerCase(), s.label]));

    const updateRows = {
        validate: (row) => {
            const ticketNumber = row.ticket_number?.toString().trim();
            const comment = row.comment?.toString().trim() || "";
            const visibility = (row.visibility || "INTERNAL").toString().trim().toUpperCase();
            const statusCell = row.status?.toString().trim() || "";
            const links = linksOf(row.attachment);
            if (!ticketNumber) return { error: "Missing ticket_number" };
            if (!comment && !statusCell && links.length === 0) return { error: "Give a comment, a status or an attachment" };
            if (visibility !== "INTERNAL" && visibility !== "EXTERNAL") {
                return { error: `visibility must be INTERNAL or EXTERNAL, got "${row.visibility}"` };
            }
            const status = statusCell ? statusByName.get(statusCell.toLowerCase()) : null;
            if (statusCell && !status) return { error: `Unknown status "${statusCell}"` };
            const bad = links.find((l) => !/^https:\/\/(drive|docs)\.google\.com\/|^https:\/\/(www\.)?dropbox\.com\//i.test(l));
            if (bad) return { error: `Attachment must be a Google Drive or Dropbox share link: ${bad}` };
            return { item: { escalationNumber: ticketNumber, body: comment || null, visibility, status, attachmentLinks: links } };
        },
        send: (items) => postGeneric(`${apiConstants.ESCALATION_DETAIL_V2}/bulk-by-number/update`, { items }),
    };

    const reset = () => {
        setFileName("");
        setResults([]);
        setProcessing(false);
        cancelRef.current = false;
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleClose = () => {
        if (processing) return;
        reset();
        onClose();
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setFileName(file.name);
        setResults([]);
        setProcessing(true);
        cancelRef.current = false;
        try {
            const rows = await readFileAsRows(file);
            if (!rows.length) {
                enqueueSnackbar("No rows found in Sheet1.", { variant: "warning" });
                return;
            }
            const withFiles = rows.some((r) => linksOf(r.attachment).length > 0);
            const finalResults = await runBulkInSlices(rows,
                { ...updateRows, ...(withFiles ? { sliceSize: SLICE_SIZE_WITH_FILES } : {}) }, setResults, cancelRef);
            const ok = finalResults.filter((r) => r.status === "Success" || r.status === "Skipped").length;
            enqueueSnackbar(`Bulk update finished: ${ok} of ${finalResults.length} rows done.`,
                { variant: ok === finalResults.length ? "success" : "warning" });
            onDone?.();
        } catch (err) {
            enqueueSnackbar(err.message || "Bulk update couldn't process the file.", { variant: "error" });
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: DIALOG_PAPER_SX }}>
            <TicketsDialogTitle icon={PlaylistAddCheckIcon} title="Bulk Update" sub="Comment, attach files and change status from one sheet" onClose={handleClose} closeDisabled={processing} />
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                    Upload a CSV or Excel file with a <b>Sheet1</b> and these columns:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: 13, color: MUTED, display: "grid", gap: 0.5 }}>
                    <li><code>ticket_number</code>: required.</li>
                    <li><code>comment</code> and <code>visibility</code> (INTERNAL or EXTERNAL, default INTERNAL).</li>
                    <li><code>status</code>: the status to move the ticket to, such as Pending at Ops or Resolved. Leave blank to keep it.</li>
                    <li><code>attachment</code>: Google Drive or Dropbox share links, comma-separated, up to 20 MB per row.</li>
                </Box>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                    Each row needs a comment, a status or an attachment. Comments and files are added first, then the status changes. Max file size 2MB.
                </Typography>

                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={processing} sx={pillBtnSx}>
                        {fileName || "Choose file"}
                        <input
                            ref={fileInputRef}
                            type="file"
                            hidden
                            accept=".csv,.xlsx,.xls"
                            onChange={(e) => {
                                if (e.target.files?.[0]?.size > 2 * 1024 * 1024) {
                                    enqueueSnackbar("File is larger than the 2MB limit.", { variant: "error" });
                                    e.target.value = "";
                                    return;
                                }
                                handleFile(e);
                            }}
                        />
                    </Button>
                    <Button
                        variant="text"
                        startIcon={<GetAppIcon />}
                        onClick={() => downloadSampleSheetWithDropdowns(SAMPLE_ROWS, "Bulk_Update_Sample.xlsx",
                            { status: statusList.map((s) => s.label) })
                            .catch(() => enqueueSnackbar("Couldn't create the sample file.", { variant: "error" }))}
                        sx={{ textTransform: "none", fontWeight: 700, color: COLORS.purple, borderRadius: "999px", "&:hover": { bgcolor: COLORS.purpleSoft } }}
                    >
                        Download Sample
                    </Button>
                </Box>

                {processing && (
                    <Box>
                        <LinearProgress sx={{ height: 4, borderRadius: 2, bgcolor: COLORS.purpleSoft, "& .MuiLinearProgress-bar": { background: ACCENTS.purple.grad } }} />
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
                            <Typography sx={{ fontSize: 13, color: MUTED }}>{results.length} processed</Typography>
                            <Button size="small" color="error" onClick={() => { cancelRef.current = true; }}>
                                Stop upload
                            </Button>
                        </Box>
                    </Box>
                )}

                {results.length > 0 && (
                    <Box sx={{ maxHeight: 260, overflowY: "auto", border: `1px solid ${COLORS.grid}`, borderRadius: "10px" }}>
                        {results.map((r, i) => (
                            <Box key={i} sx={{
                                display: "flex", justifyContent: "space-between", gap: 2, px: 1.5, py: 0.75,
                                fontSize: 13, borderBottom: "1px solid #F1EFF5",
                                color: r.status === "Success" ? ACCENTS.green.solid : r.status === "Skipped" ? COLORS.ink2 : ACCENTS.red.solid,
                            }}>
                                <span style={{ whiteSpace: "nowrap" }}>{r.ticketNumber}</span>
                                <span style={{ textAlign: "right" }}>{r.message}</span>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <LoadingButton variant="contained" onClick={handleClose} loading={processing} disabled={processing} sx={gradientBtnSx}>
                    Done
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default BulkUpdateModal;
